import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faUser, faXmark } from "@fortawesome/free-solid-svg-icons";

import { UserContext } from "../contexts/UserContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const COPY = {
  menu: { en: "Menu", pl: "Menu", es: "Menú" },
  close: { en: "Close menu", pl: "Zamknij menu", es: "Cerrar menú" },
  profile: { en: "Profile", pl: "Profil", es: "Perfil" },
  login: { en: "Log in", pl: "Zaloguj", es: "Iniciar sesión" },
  home: { en: "Home", pl: "Start", es: "Inicio" },
  training: { en: "Training", pl: "Trening", es: "Entrenamiento" },
  longGame: { en: "Long Game", pl: "Long Game", es: "Partida larga" },
  hood: { en: "Hood AI", pl: "Hood AI", es: "Hood AI" },
  analysis: { en: "AI Analysis", pl: "Analiza AI", es: "Análisis IA" },
  stats: { en: "Statistics", pl: "Statystyki", es: "Estadísticas" },
  basics: { en: "Basics", pl: "Podstawy", es: "Conceptos básicos" },
  lessons: { en: "Lessons", pl: "Lekcje", es: "Clases" },
  clubs: { en: "Clubs", pl: "Kluby", es: "Clubes" },
  contact: { en: "Contact", pl: "Kontakt", es: "Contacto" },
  beta: { en: "Hood AI Beta", pl: "Hood AI Beta", es: "Hood AI Beta" },
};

const BASE_ITEMS = [
  ["home", "/"],
  ["hood", "/hood-ai"],
  ["lessons", "/meetings"],
  ["clubs", "/chessclub"],
  ["contact", "/contact"],
];

function pick(copy, lang) {
  return copy?.[lang] || copy?.en || "";
}

export default function ResponsiveAppMenu({ className = "" }) {
  const { firebaseUser } = useContext(UserContext);
  const { language } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const lang = ["en", "pl", "es"].includes(language) ? language : "en";

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = BASE_ITEMS;

  return (
    <div ref={wrapRef} className={`ch-app-menu ${className}`.trim()}>
      <style>{`
        .ch-app-menu{
          position:relative;
          z-index:2000;
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:8px;
          font-family:inherit;
        }
        .ch-app-menu-toggle,
        .ch-app-menu-profile,
        .ch-app-menu-link{
          min-height:38px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:7px;
          border-radius:999px;
          border:1px solid rgba(255,213,116,.46);
          background:rgba(7,18,41,.88);
          color:#fff2cb;
          text-decoration:none;
          font-size:12px;
          font-weight:900;
          line-height:1;
          padding:9px 12px;
          box-shadow:0 8px 22px rgba(0,0,0,.18);
          backdrop-filter:blur(7px);
          cursor:pointer;
          white-space:nowrap;
        }
        .ch-app-menu-toggle{
          width:42px;
          padding:0;
          font-size:18px;
        }
        .ch-app-menu-profile{
          border-color:rgba(125,211,252,.62);
          color:#dff6ff;
        }
        .ch-app-menu-profile svg{
          color:#7dd3fc;
        }
        .ch-app-menu-link:hover,
        .ch-app-menu-link:focus-visible,
        .ch-app-menu-profile:hover,
        .ch-app-menu-profile:focus-visible{
          color:#fff;
          background:rgba(255,213,116,.14);
          outline:none;
        }
        .ch-app-menu-link.is-active{
          border-color:#ffd400;
          color:#ffd400;
          background:rgba(255,212,0,.10);
        }
        .ch-app-menu-link.is-beta{
          border-color:rgba(125,211,252,.68);
          color:#7dd3fc;
        }
        .ch-app-menu-desktop{
          display:flex;
          align-items:center;
          gap:6px;
          flex-wrap:wrap;
          justify-content:flex-end;
        }
        .ch-app-menu-mobile{
          display:none;
        }
        .ch-app-menu-drawer{
          position:absolute;
          right:0;
          top:calc(100% + 8px);
          width:min(86vw,320px);
          padding:10px;
          border:1px solid rgba(255,213,116,.35);
          border-radius:18px;
          background:rgba(6,18,39,.98);
          box-shadow:0 20px 48px rgba(0,0,0,.38);
          display:grid;
          gap:7px;
        }
        .ch-app-menu-drawer .ch-app-menu-link{
          width:100%;
          justify-content:flex-start;
          border-radius:12px;
          padding:12px 14px;
        }
        @media (max-width: 768px){
          .ch-app-menu-desktop{display:none;}
          .ch-app-menu-mobile{
            display:flex;
            align-items:center;
            gap:8px;
          }
        }
      `}</style>

      <div className="ch-app-menu-desktop" aria-label={pick(COPY.menu, lang)}>
        {items.map(([key, route]) => (
          <Link
            key={key}
            to={route}
            className={`ch-app-menu-link${location.pathname === route ? " is-active" : ""}${key === "beta" ? " is-beta" : ""}`}
          >
            {pick(COPY[key], lang)}
          </Link>
        ))}
        <Link
          to={firebaseUser ? "/profile" : "/login"}
          className="ch-app-menu-profile"
        >
          <FontAwesomeIcon icon={faUser} />
          {firebaseUser ? pick(COPY.profile, lang) : pick(COPY.login, lang)}
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="ch-app-menu-mobile">
        <button
          type="button"
          className="ch-app-menu-toggle"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={open ? pick(COPY.close, lang) : pick(COPY.menu, lang)}
          title={open ? pick(COPY.close, lang) : pick(COPY.menu, lang)}
        >
          <FontAwesomeIcon icon={open ? faXmark : faBars} />
        </button>

        <Link
          to={firebaseUser ? "/profile" : "/login"}
          className="ch-app-menu-profile"
        >
          <FontAwesomeIcon icon={faUser} />
          {firebaseUser ? pick(COPY.profile, lang) : pick(COPY.login, lang)}
        </Link>

        {open && (
          <nav className="ch-app-menu-drawer" aria-label={pick(COPY.menu, lang)}>
            {items.map(([key, route]) => (
              <Link
                key={key}
                to={route}
                className={`ch-app-menu-link${location.pathname === route ? " is-active" : ""}${key === "beta" ? " is-beta" : ""}`}
              >
                {pick(COPY[key], lang)}
              </Link>
            ))}
            <div style={{ padding: "6px 8px 2px" }}>
              <LanguageSwitcher />
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
